/**
 * Settings page component.
 * Allows configuration of API endpoint and other settings.
 *
 * @module pages/SettingsPage
 */

import { useState, useEffect } from 'react';
import { Save, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Card, CardHeader, CardBody, CardFooter, Button, Input } from '@/components/ui';
import { config } from '@/config';
import { updateBaseUrl } from '@/services';
import { showErrorToast } from '@/utils/errorToast';

interface SettingsState {
  apiBaseUrl: string;
}

/**
 * Settings page for configuring the application
 */
export function SettingsPage() {
  const [settings, setSettings] = useState<SettingsState>({
    apiBaseUrl: '',
  });
  const [savedSettings, setSavedSettings] = useState<SettingsState>({
    apiBaseUrl: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Load settings on mount
  useEffect(() => {
    // Try to load from localStorage first, fallback to config
    const storedApiUrl = localStorage.getItem('adc_api_base_url');
    const currentUrl = storedApiUrl || config.apiBaseUrl;

    setSettings({ apiBaseUrl: currentUrl });
    setSavedSettings({ apiBaseUrl: currentUrl });
  }, []);

  const hasChanges = settings.apiBaseUrl !== savedSettings.apiBaseUrl;

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Save to localStorage
      localStorage.setItem('adc_api_base_url', settings.apiBaseUrl);

      // Update the HTTP client
      updateBaseUrl(settings.apiBaseUrl);

      setSavedSettings({ ...settings });
      toast.success('Settings saved successfully');
    } catch {
      showErrorToast('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({ ...savedSettings });
  };

  const handleResetToDefault = () => {
    const defaultUrl = config.apiBaseUrl;
    setSettings({ apiBaseUrl: defaultUrl });
    localStorage.removeItem('adc_api_base_url');
    updateBaseUrl(defaultUrl);
    setSavedSettings({ apiBaseUrl: defaultUrl });
    toast.success('Settings reset to defaults');
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        description="Configure application settings"
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Settings' }]}
      />

      <div className="max-w-2xl space-y-6">
        {/* API Configuration */}
        <Card>
          <CardHeader
            title="API Configuration"
            description="Configure the connection to the ADC Global API"
          />
          <CardBody className="space-y-4">
            <Input
              label="API Base URL"
              placeholder="http://localhost:5000"
              value={settings.apiBaseUrl}
              onChange={(e) => setSettings({ ...settings, apiBaseUrl: e.target.value })}
              helperText="The base URL of the ADC Global API server"
            />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p>
                <strong>Current configured URL:</strong>{' '}
                <code className="text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#161616] px-1 py-0.5 rounded">
                  {savedSettings.apiBaseUrl}
                </code>
              </p>
              <p className="mt-1">
                <strong>Default URL:</strong>{' '}
                <code className="text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#161616] px-1 py-0.5 rounded">
                  {config.apiBaseUrl}
                </code>
              </p>
            </div>
          </CardBody>
          <CardFooter>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={handleResetToDefault}>
                Reset to Default
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={handleReset}
                  disabled={!hasChanges}
                  leftIcon={<RefreshCw className="h-4 w-4" />}
                >
                  Discard Changes
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges}
                  isLoading={isSaving}
                  leftIcon={<Save className="h-4 w-4" />}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
